import React from 'react';
import { compose } from 'ramda';
import { Button, Input, Checkbox } from '@deeptrue-frontend/ui';
import { Formik, Form, Field as FormikField, FormikHelpers } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import RouterProvider from 'components/RouterProvider/RouterProvider';

import Fields from 'components/Fields';
import IntlProvider, {
  useFormatMessage,
  IntlProviderType,
} from 'components/IntlProvider';

const { Link, ROUTES } = RouterProvider;

const useStyles = makeStyles((theme) => ({
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  small: {
    fontSize: '0.875rem',
    fontWeight: 700,
  },
  link: {
    '&:hover': {
      opacity: 0.6,
    },
  },
}));

export type SubmitHandler = (
  values: any,
  formHelpers: FormikHelpers<any>
) => void;

interface WorkbenchInfoProps {
  onSubmit: SubmitHandler;
  intl: IntlProviderType;
  initialValues: {
    isEditMode?: boolean;
    products: { [k: string]: any }[];
  };
}

const WorkbenchInfo = ({ onSubmit, initialValues }: WorkbenchInfoProps) => {
  const classes = useStyles();
  const t = useFormatMessage();

  return (
    <Formik onSubmit={onSubmit} initialValues={initialValues} noValidate>
      {({ touched, isSubmitting, errors }) => (
        <Form className={classes.form}>
          {initialValues.isEditMode && (
            <FormikField
              name="id"
              as={Input}
              hasError={touched.normal && errors.normal}
              helperText={touched.normal && errors.normal}
              label={t('forms.workbench.id', 'Workbench ID')}
            />
          )}
          <FormikField
            name="name"
            as={Input}
            hasError={touched.normal && errors.normal}
            helperText={touched.normal && errors.normal}
            label={t('forms.workbench.name', 'Workbench name')}
          />
          <FormikField
            name="slug"
            as={Input}
            hasError={touched.normal && errors.normal}
            helperText={touched.normal && errors.normal}
            label={t('forms.workbench.slug', 'Workbench slug')}
          />
          <FormikField
            name="icon"
            component={Fields.File}
            type="file"
            label={t('forms.workbench.icon', 'Workbench icon')}
          />
          <div style={{ marginTop: '2rem' }}>
            <FormikField
              name="active"
              as={Checkbox}
              type="checkbox"
              label={t(
                'forms.workbench.active',
                'Do you want it to be active?'
              )}
            />
          </div>

          {initialValues.isEditMode && (
            <>
              <p className={classes.small}>
                {t('forms.workbench.products', 'Workbench related products')}
              </p>
              {initialValues.products.length === 0 ? (
                <p>N/A</p>
              ) : (
                initialValues.products.map((product: any) => (
                  <p key={product.slug}>
                    <Link
                      className={classes.link}
                      to={`${ROUTES.ADMIN.PRODUCTS.LIST}/${product.slug}`}
                    >
                      {product.name}
                    </Link>
                  </p>
                ))
              )}
            </>
          )}
          <Button
            mt="2rem"
            width="100%"
            height="3rem"
            type="submit"
            disabled={isSubmitting}
            isLoading={isSubmitting}
          >
            {t('forms.profile.save', 'Save')}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default compose(IntlProvider.withIntl)(WorkbenchInfo);
